import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBugsByIds, getMyBugs, downloadAttachment, updateBug, deleteAttachment, uploadAttachments } from '../api';
import './TrackBugs.css';

function TrackBugs() {
    const [bugs, setBugs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedBug, setExpandedBug] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [editingBug, setEditingBug] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [newFiles, setNewFiles] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const email = localStorage.getItem('userEmail');
        const token = localStorage.getItem('userToken');
        
        if (email && token) {
            setIsLoggedIn(true);
            setUserEmail(email);
            fetchMyBugs();
        } else {
            fetchBugsByLocalIds();
        }
    }, []);

    const fetchMyBugs = async () => {
        setIsLoading(true);
        setError('');
        try {
            const result = await getMyBugs();
            setBugs(result);
        } catch (err) {
            setError('Failed to fetch feedback reports.');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchBugsByLocalIds = async () => {
        setIsLoading(true);
        setError('');
        
        const savedBugIds = JSON.parse(localStorage.getItem('submittedBugIds') || '[]');
        
        if (savedBugIds.length === 0) {
            setBugs([]);
            setIsLoading(false);
            return;
        }

        try {
            const result = await getBugsByIds(savedBugIds);
            setBugs(result);
        } catch (err) {
            setError('Failed to fetch feedback reports.');
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            submitted: '#F59E0B',
            in_review: '#3B82F6',
            in_progress: '#8B5CF6',
            resolved: '#10B981',
            closed: '#6B7280'
        };
        return colors[status] || '#6B7280';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const toggleExpand = (bugId) => {
        if (editingBug === bugId) return;
        setExpandedBug(expandedBug === bugId ? null : bugId);
    };

    const handleDownload = async (bugId, attachment) => {
        try {
            const response = await downloadAttachment(bugId, attachment.s3_key);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download file:', err);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const startEdit = (bug) => {
        setEditingBug(bug.id);
        setEditForm({
            phone_model: bug.phone_model || '',
            system_version: bug.system_version || '',
            software: bug.software || '',
            bug_description: bug.bug_description || '',
            steps_to_reproduce: bug.steps_to_reproduce || '',
            expected_behavior: bug.expected_behavior || '',
            actual_behavior: bug.actual_behavior || '',
            email: bug.email || '',
            name: bug.name || '',
            security_version: bug.security_version || '',
            kaios_api_level: bug.kaios_api_level || '',
            country: bug.country || '',
            payment_facility: bug.payment_facility || '',
        });
        setNewFiles([]);
        setSaveError('');
    };

    const cancelEdit = () => {
        setEditingBug(null);
        setEditForm({});
        setNewFiles([]);
        setSaveError('');
    };

    const handleEditChange = (field, value) => {
        setEditForm(prev => ({ ...prev, [field]: value }));
    };

    const handleDeleteAttachment = async (bugId, attachment) => {
        try {
            const updated = await deleteAttachment(bugId, attachment.s3_key);
            setBugs(prev => prev.map(b => b.id === bugId ? updated : b));
        } catch (err) {
            console.error('Failed to delete attachment:', err);
        }
    };

    const handleSaveEdit = async (bugId) => {
        setIsSaving(true);
        setSaveError('');
        try {
            const updated = await updateBug(bugId, editForm);
            
            if (newFiles.length > 0) {
                try {
                    const withAttachments = await uploadAttachments(bugId, newFiles);
                    setBugs(prev => prev.map(b => b.id === bugId ? withAttachments : b));
                } catch (uploadErr) {
                    setBugs(prev => prev.map(b => b.id === bugId ? updated : b));
                    console.error('Failed to upload new attachments:', uploadErr);
                }
            } else {
                setBugs(prev => prev.map(b => b.id === bugId ? updated : b));
            }
            
            setEditingBug(null);
            setEditForm({});
            setNewFiles([]);
        } catch (err) {
            setSaveError(err.response?.data?.detail || 'Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderEditField = (label, field, type = 'text') => (
        <div className="edit-field">
            <label className="edit-label">{label}</label>
            {type === 'textarea' ? (
                <textarea
                    className="edit-input edit-textarea"
                    value={editForm[field] || ''}
                    onChange={e => handleEditChange(field, e.target.value)}
                />
            ) : (
                <input
                    type="text"
                    className="edit-input"
                    value={editForm[field] || ''}
                    onChange={e => handleEditChange(field, e.target.value)}
                />
            )}
        </div>
    );

    return (
        <div className="track-bugs-page">
            <div className="hero-section">
                <h1 className="hero-title">Track Your<br />Feedback</h1>
                <p className="hero-subtitle">
                    {isLoggedIn 
                        ? `Viewing all feedback reports for ${userEmail}`
                        : 'Showing feedback reports submitted from this browser'}
                </p>
                <div className="hero-decoration"></div>
            </div>

            {!isLoggedIn && (
                <div className="login-prompt">
                    <p>
                        <strong>Want to see all your feedback reports?</strong> 
                        <button 
                            className="link-button"
                            onClick={() => navigate('/login')}
                        >
                            Log in
                        </button>
                        to view all feedback associated with your email.
                    </p>
                </div>
            )}

            {error && (
                <div className="error-banner">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading feedback reports...</p>
                </div>
            ) : (
                <div className="results-container">
                    {bugs.length === 0 ? (
                        <div className="no-results">
                            {isLoggedIn ? (
                                <p>No feedback reports found for your account.</p>
                            ) : (
                                <p>No feedback reports found. Submit feedback to track it here.</p>
                            )}
                        </div>
                    ) : (
                        <div className="bugs-list">
                            <h2 className="results-title">
                                {bugs.length} feedback report{bugs.length !== 1 ? 's' : ''}
                            </h2>
                            {bugs.map((bug) => (
                                <div 
                                    key={bug.id} 
                                    className={`bug-card ${expandedBug === bug.id ? 'expanded' : ''}`}
                                    onClick={() => toggleExpand(bug.id)}
                                >
                                    <div className="bug-header">
                                        <div className="bug-info">
                                            <span className="bug-id">#{bug.id.slice(0, 8)}</span>
                                            <h3 className="bug-software">{bug.software}</h3>
                                            <span 
                                                className="bug-status"
                                                style={{ backgroundColor: getStatusColor(bug.status) }}
                                            >
                                                {bug.status.replace('_', ' ')}
                                            </span>
                                        </div>
                                        <div className="bug-meta">
                                            <span className="bug-date">{formatDate(bug.created_at)}</span>
                                        </div>
                                    </div>

                                    {expandedBug === bug.id && editingBug === bug.id ? (
                                        <div className="bug-details edit-mode" onClick={e => e.stopPropagation()}>
                                            <div className="detail-section">
                                                <h4 className="detail-section-title">Device Information</h4>
                                                {renderEditField('Device Model', 'phone_model')}
                                                {renderEditField('System Version', 'system_version')}
                                                {renderEditField('Security Version', 'security_version')}
                                                {renderEditField('KaiOS API Level', 'kaios_api_level')}
                                                {renderEditField('Country', 'country')}
                                                {renderEditField('Payment Facility', 'payment_facility')}
                                            </div>

                                            <div className="detail-section">
                                                <h4 className="detail-section-title">Feedback Details</h4>
                                                {renderEditField('App version', 'software')}
                                                {renderEditField('Description', 'bug_description', 'textarea')}
                                                {renderEditField('Steps to Reproduce', 'steps_to_reproduce', 'textarea')}
                                            </div>

                                            <div className="detail-section">
                                                <h4 className="detail-section-title">Attachments</h4>
                                                {bug.attachments && bug.attachments.length > 0 && (
                                                    <div className="attachments-list">
                                                        {bug.attachments.map((att, idx) => (
                                                            <div key={idx} className="attachment-edit-row">
                                                                <span className="attachment-name">{att.filename} ({formatFileSize(att.size)})</span>
                                                                <button
                                                                    className="attachment-delete-btn"
                                                                    onClick={() => handleDeleteAttachment(bug.id, att)}
                                                                    title="Delete attachment"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="new-files-section">
                                                    <input
                                                        type="file"
                                                        multiple
                                                        ref={fileInputRef}
                                                        style={{ display: 'none' }}
                                                        onChange={e => setNewFiles(prev => [...prev, ...Array.from(e.target.files)])}
                                                    />
                                                    <button
                                                        className="add-file-btn"
                                                        onClick={() => fileInputRef.current?.click()}
                                                    >
                                                        + Add Files
                                                    </button>
                                                    {newFiles.length > 0 && (
                                                        <div className="new-files-list">
                                                            {newFiles.map((f, idx) => (
                                                                <div key={idx} className="attachment-edit-row">
                                                                    <span className="attachment-name">{f.name} ({formatFileSize(f.size)})</span>
                                                                    <button
                                                                        className="attachment-delete-btn"
                                                                        onClick={() => setNewFiles(prev => prev.filter((_, i) => i !== idx))}
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="detail-section">
                                                <h4 className="detail-section-title">Contact Information</h4>
                                                {renderEditField('Name', 'name')}
                                                <div className="edit-field">
                                                    <label className="edit-label">Email</label>
                                                    <input type="text" className="edit-input" value={editForm.email || ''} disabled />
                                                </div>
                                            </div>

                                            {saveError && <div className="save-error">{saveError}</div>}

                                            <div className="edit-actions">
                                                <button className="btn btn-save" onClick={() => handleSaveEdit(bug.id)} disabled={isSaving}>
                                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                                </button>
                                                <button className="btn btn-cancel" onClick={cancelEdit} disabled={isSaving}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : expandedBug === bug.id ? (
                                        <div className="bug-details" onClick={e => e.stopPropagation()}>
                                            {isLoggedIn && (
                                                <div className="edit-button-row">
                                                    <button className="btn btn-edit" onClick={() => startEdit(bug)}>
                                                        Edit
                                                    </button>
                                                </div>
                                            )}
                                            <div className="detail-section">
                                                <h4 className="detail-section-title">Device Information</h4>
                                                <div className="detail-row">
                                                    <span className="detail-label">Device Model:</span>
                                                    <span className="detail-value">{bug.phone_model}</span>
                                                </div>
                                                <div className="detail-row">
                                                    <span className="detail-label">System Version:</span>
                                                    <span className="detail-value">{bug.system_version}</span>
                                                </div>
                                                {bug.security_version && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Security Version:</span>
                                                        <span className="detail-value">{bug.security_version}</span>
                                                    </div>
                                                )}
                                                {bug.kaios_api_level && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">KaiOS API Level:</span>
                                                        <span className="detail-value">{bug.kaios_api_level}</span>
                                                    </div>
                                                )}
                                                {bug.country && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Country:</span>
                                                        <span className="detail-value">{bug.country}</span>
                                                    </div>
                                                )}
                                                {bug.payment_facility && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Payment Facility:</span>
                                                        <span className="detail-value">{bug.payment_facility}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="detail-section">
                                                <h4 className="detail-section-title">Feedback Details</h4>
                                                <div className="detail-row">
                                                    <span className="detail-label">App version:</span>
                                                    <span className="detail-value">{bug.software}</span>
                                                </div>
                                                <div className="detail-row full-width">
                                                    <span className="detail-label">Description:</span>
                                                    <p className="detail-value">{bug.bug_description}</p>
                                                </div>
                                                {bug.steps_to_reproduce && (
                                                    <div className="detail-row full-width">
                                                        <span className="detail-label">Steps to Reproduce:</span>
                                                        <p className="detail-value">{bug.steps_to_reproduce}</p>
                                                    </div>
                                                )}
                                                {bug.attachments && bug.attachments.length > 0 && (
                                                    <div className="detail-row full-width">
                                                        <span className="detail-label">Attachments:</span>
                                                        <div className="attachments-list">
                                                            {bug.attachments.map((att, idx) => (
                                                                <button
                                                                    key={idx}
                                                                    className="attachment-link"
                                                                    onClick={() => handleDownload(bug.id, att)}
                                                                >
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                                        <polyline points="7 10 12 15 17 10"></polyline>
                                                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                                                    </svg>
                                                                    <span>{att.filename}</span>
                                                                    <span className="attachment-size">({formatFileSize(att.size)})</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="detail-section">
                                                <h4 className="detail-section-title">Contact Information</h4>
                                                <div className="detail-row">
                                                    <span className="detail-label">Email:</span>
                                                    <span className="detail-value">{bug.email}</span>
                                                </div>
                                                {bug.name && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Name:</span>
                                                        <span className="detail-value">{bug.name}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {bug.admin_replies && bug.admin_replies.length > 0 && (
                                                <div className="admin-replies">
                                                    <h4>Admin Replies</h4>
                                                    {bug.admin_replies.map((reply, index) => (
                                                        <div key={index} className="reply-item">
                                                            <p className="reply-message">{reply.message}</p>
                                                            <span className="reply-date">{formatDate(reply.created_at)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : null}

                                    <div className="expand-indicator">
                                        {expandedBug === bug.id ? '▲ Click to collapse' : '▼ Click to expand'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TrackBugs;
